import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Sparkles, CreditCard, CalendarHeart } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/sections/Footer";
import ScrollProgress from "../components/ScrollProgress";
import { useLenis } from "../lib/useLenis";
import { useContent } from "../lib/contentContext";

const CHERRY_CONFIG = {
    debug: false,
    variables: {
        slug: "overall-beauty-wellness",
        name: "Overall Beauty & Wellness",
        images: [26],
        customLogo: "",
        defaultPurchaseAmount: 750,
        customImage: "",
        imageCategory: "medspa",
        language: "en",
    },
    styles: {
        primaryColor: "#B08D2F",
        secondaryColor: "#B08D2F14",
        fontFamily: "Montserrat",
        headerFontFamily: "Playfair Display",
    },
};
const CHERRY_SECTIONS = ["hero", "calculator", "howitworks", "faq"];

function ensureFonts() {
    const ID = "cherry-fonts";
    if (document.getElementById(ID)) return;
    const link = document.createElement("link");
    link.id = ID;
    link.rel = "stylesheet";
    link.href =
        "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400..900&family=Montserrat:wght@100..900&display=swap";
    document.head.appendChild(link);
}

export default function Financing() {
    useLenis();
    const { content } = useContent();
    const bookingUrl = content?.contact?.booking_url || "/book-form";
    const cherryUrl =
        content?.contact?.cherry_url ||
        "https://pay.withcherry.com/overall-beauty-wellness";
    const [widgetEmpty, setWidgetEmpty] = useState(false);

    useEffect(() => {
        const prev = document.title;
        document.title = "Financing & Payment Plans — Overall Beauty & Wellness";
        window.scrollTo({ top: 0, behavior: "instant" });

        ensureFonts();

        // Mirror the vendor bootstrap: create the queue if the script hasn't loaded yet.
        window._hw =
            window._hw ||
            function () {
                (window._hw.q = window._hw.q || []).push(arguments);
            };

        // Render (or re-render on revisit) into the divs below.
        window._hw("init", CHERRY_CONFIG, CHERRY_SECTIONS);

        const SCRIPT_ID = "_hw";
        if (!document.getElementById(SCRIPT_ID)) {
            const js = document.createElement("script");
            js.id = SCRIPT_ID;
            js.src = "https://files.withcherry.com/widgets/widget.js";
            js.async = true;
            document.body.appendChild(js);
        }

        // If the embed is blocked (ad-blocker / network) or fails to render,
        // the target divs stay empty — show a direct-apply fallback instead.
        const checkTimer = setTimeout(() => {
            const filled = CHERRY_SECTIONS.some((id) => {
                const el = document.getElementById(id);
                return el && el.innerHTML.trim().length > 0;
            });
            setWidgetEmpty(!filled);
        }, 4500);

        return () => {
            clearTimeout(checkTimer);
            document.title = prev;
        };
    }, []);

    const trust = [
        {
            icon: CreditCard,
            label: "0% Options Available",
            body: "Qualified applicants may access promotional 0% APR plans on eligible treatments.",
        },
        {
            icon: ShieldCheck,
            label: "No Impact to Credit",
            body: "Checking your options won't affect your credit score.",
        },
        {
            icon: Sparkles,
            label: "Instant Decision",
            body: "See what you qualify for in seconds and book your treatment with confidence.",
        },
    ];

    return (
        <main className="grain relative bg-ink text-white" data-testid="financing-page">
            <ScrollProgress />
            <Nav />

            {/* Brand hero */}
            <section
                className="relative overflow-hidden border-b border-white/5 pt-14 sm:pt-20 pb-10 sm:pb-14"
                data-testid="financing-hero"
            >
                <motion.div
                    aria-hidden
                    animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.85, 0.5] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                    className="pointer-events-none absolute -top-24 right-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-3xl"
                />
                <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
                    <Link
                        to="/"
                        data-testid="financing-back-home"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-gold"
                    >
                        <ArrowLeft size={14} /> Back to Home
                    </Link>

                    <div className="mt-8 max-w-3xl">
                        <div className="label text-gold">Book Consultation · Financing</div>
                        <h1
                            className="mt-4 font-serif text-4xl leading-[1.05] sm:text-6xl"
                            data-testid="financing-title"
                        >
                            Beautiful results,
                            <br />
                            <span className="italic text-gold">on your terms.</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-white/60 sm:text-lg">
                            Reserve your complimentary consultation and pre-qualify for
                            flexible monthly payment plans through Cherry — so you can
                            plan the treatments you love without the wait.
                        </p>

                        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <a
                                href={cherryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="financing-apply-cta"
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-ink transition-all duration-300 hover:bg-gold-soft hover:shadow-[0_0_30px_-4px_rgba(212,175,55,0.6)]"
                            >
                                <CreditCard size={16} />
                                Apply with Cherry
                            </a>
                            <a
                                href={bookingUrl}
                                data-testid="financing-book-visit"
                                className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-white/80 transition-all duration-300 hover:border-gold/60 hover:text-gold"
                            >
                                <CalendarHeart size={16} />
                                Book a Visit Instead
                            </a>
                        </div>
                    </div>

                    <div className="mt-10 grid gap-4 sm:grid-cols-3">
                        {trust.map((t, i) => (
                            <div
                                key={t.label}
                                data-testid={`financing-trust-${i}`}
                                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur"
                            >
                                <t.icon className="text-gold" size={20} />
                                <div className="mt-3 text-sm font-semibold tracking-wide text-white/90">
                                    {t.label}
                                </div>
                                <p className="mt-1 text-sm text-white/55">{t.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cherry widget — rendered on a light card so its sections read cleanly */}
            <section id="apply" className="relative py-12 sm:py-16 scroll-mt-24" data-testid="cherry-widget-section">
                <div className="mx-auto max-w-6xl px-4 sm:px-8">
                    <div className="overflow-hidden rounded-3xl bg-cream text-ink shadow-2xl ring-1 ring-black/5">
                        <div id="all" />
                        <div id="hero" />
                        <div id="calculator" />
                        <div id="howitworks" />
                        <div id="testimony" />
                        <div id="faq" />
                        {widgetEmpty && (
                            <div
                                className="px-6 py-14 text-center sm:px-10"
                                data-testid="cherry-fallback"
                            >
                                <CreditCard
                                    className="mx-auto text-gold-dark"
                                    size={28}
                                />
                                <h3 className="mt-4 font-serif text-2xl italic text-ink">
                                    Apply for financing with Cherry
                                </h3>
                                <p className="mx-auto mt-3 max-w-md text-sm text-ink/60">
                                    The calculator couldn't load in your browser —
                                    an ad blocker or network setting may be
                                    blocking it. You can open Cherry's secure
                                    application directly. It takes about a minute
                                    and won't affect your credit score.
                                </p>
                                <a
                                    href={cherryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="cherry-fallback-cta"
                                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors hover:bg-gold-dark"
                                >
                                    <CreditCard size={16} /> Open Cherry Application
                                </a>
                            </div>
                        )}
                    </div>
                    <p className="mt-6 text-center text-xs text-white/40">
                        Financing provided by Cherry Technologies, Inc. Subject to
                        eligibility and approval. Rates and terms may vary.
                    </p>
                    <div className="mt-4 text-center">
                        <a
                            href={cherryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cherry-direct-link"
                            className="link-underline text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-soft"
                        >
                            Prefer to apply directly? Open the Cherry application →
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
